import React, { createContext, FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ITokenData } from '../hooks/useAuth';
import { parseAccessToken } from '../helpers/parseAccessToken';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { IMfaDevice } from '../models/IMfaDevice';
import { useEnumState } from '../hooks/useEnumState';

interface IBaseAccountData {
  userId: string;
  tokenData: ITokenData;
  email: string;
}

type IIdentityData = IBaseAccountData;

interface IAuthContext {
  identities: IIdentityData[];

  identity: IIdentityData | null;
  setIdentity: (identity: IIdentityData | null) => void;
  updateToken: (tokenData: ITokenData) => void;
  getToken: () => ITokenData | null;
  forgetCurrentIdentity: () => void;
  forgetIdentity: (email: string) => void;

  auth2faCache: null | {
    email: string;
    access_token: string;
    message: string;
    devices: IMfaDevice[];
  };
  setAuth2faCache: (data: IAuthContext['auth2faCache']) => void;

  changedExternally: boolean;
  resetChangedExternally: () => void;
}

const noop = () => null;

const defaultAuthContext: IAuthContext = {
  identities: [],

  identity: null,
  setIdentity: noop,
  updateToken: noop,
  getToken: noop,
  forgetCurrentIdentity: () => void 0,
  forgetIdentity: () => void 0,

  auth2faCache: null,
  setAuth2faCache: noop,

  changedExternally: false,
  resetChangedExternally: () => void 0,
};

export const AuthContext = createContext<IAuthContext>(defaultAuthContext);

interface IIdentitiesData {
  current: string | null;
  identities: IIdentityData[];
}

const LOCAL_STORAGE_IDENTITIES_DATA_KEY = 'identitiesData';

const SESSION_STORAGE_2FA_CACHE_KEY = '2faCache';

const ss2faCacheItem = sessionStorage.getItem(SESSION_STORAGE_2FA_CACHE_KEY);

const updateSSData = (data: unknown | null, key: string) => {
  if (data) {
    sessionStorage.setItem(key, JSON.stringify(data));
  } else {
    sessionStorage.removeItem(key);
  }
};

export const AuthProvider: FC = ({ children }) => {
  const [changedExternally, setChangedExternally, setChangedInternally] = useEnumState(false, true, false);

  const onExternalChange = useCallback(
    (newState: IIdentitiesData | null) => {
      if (newState?.current && newState.current !== identityRef.current?.email) setChangedExternally();
    },
    [setChangedExternally]
  );

  const [identities, setIdentities] = useLocalStorage<IIdentitiesData>(
    LOCAL_STORAGE_IDENTITIES_DATA_KEY,
    {
      current: null,
      identities: [],
    },
    { onExternalChange }
  );

  const identity = identities.identities.find(({ email }) => email === identities.current) ?? null;

  const identityRef = useRef<IBaseAccountData | null>(identity);
  identityRef.current = identity;

  const setIdentity = useCallback(
    (newIdentity: IIdentityData | null) => {
      const updatedIdentities = { ...identities };

      if (newIdentity !== null) {
        if (updatedIdentities.identities.find(({ email }) => email === newIdentity.email)) {
          updatedIdentities.identities = updatedIdentities.identities.map((identity) => {
            if (identity.email === newIdentity.email) {
              return newIdentity;
            }
            return identity;
          });
        } else {
          updatedIdentities.identities = [...updatedIdentities.identities, newIdentity];
        }

        updatedIdentities.current = newIdentity.email;
      } else {
        updatedIdentities.current = null;
      }

      setIdentities(updatedIdentities);
      setChangedInternally();
    },
    [identities]
  );

  const updateToken = useCallback((tokenData: ITokenData) => {
    setIdentities((identities) => {
      const parsedToken = parseAccessToken(tokenData.access_token);
      if (parsedToken.email !== identities.current) {
        throw new Error('incompatible access token');
      }

      const updatedIdentities = { ...identities };

      updatedIdentities.identities = updatedIdentities.identities.map((identity) => {
        if (identity.email === identities.current) {
          return {
            ...identity,
            tokenData: tokenData,
          };
        }
        return identity;
      });

      return updatedIdentities;
    });
  }, []);

  const forgetCurrentIdentity = useCallback(() => {
    setIdentities((currentIdentities) => {
      return {
        current: null,
        identities: currentIdentities.identities.filter((identity) => identity.email !== currentIdentities.current),
      };
    });
    setChangedInternally();
  }, []);

  const forgetIdentity = useCallback((email: string) => {
    setIdentities((currentIdentities) => {
      return {
        current: currentIdentities.current === email ? null : currentIdentities.current,
        identities: currentIdentities.identities.filter((identity) => identity.email !== email),
      };
    });
    setChangedInternally();
  }, []);

  const [auth2faCache, setAuth2faCache] = useState<IAuthContext['auth2faCache']>(
    ss2faCacheItem ? JSON.parse(ss2faCacheItem) : null
  );

  useEffect(() => {
    updateSSData(auth2faCache, SESSION_STORAGE_2FA_CACHE_KEY);
  }, [auth2faCache]);

  const getToken = useCallback(() => {
    const data = identityRef.current;
    return data?.tokenData ?? null;
  }, []);

  const contextValue = useMemo(
    () => ({
      identities: identities.identities,

      identity,
      setIdentity,
      updateToken,
      getToken,
      forgetCurrentIdentity,
      forgetIdentity,

      auth2faCache,
      setAuth2faCache,

      changedExternally,
      resetChangedExternally: setChangedInternally,
    }),
    [identities, identity, auth2faCache, changedExternally]
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};
